import { createSignal, createEffect, For, Show, Suspense } from "solid-js";
import { A, createAsync, useSearchParams, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Skeleton, EmptyState } from "~/components/ui";
import { NewDossierModal } from "~/components/modals";
import { getDossiers } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import { Plus, FolderOpen, FileText, Tag } from "lucide-solid";

export const route = {
  preload: () => getDossiers(),
} satisfies RouteDefinition;

export default function DossiersPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render DossiersPage");
  }
  const dossiers = createAsync(() => getDossiers(), { deferStream: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNewDossierModal, setShowNewDossierModal] = createSignal(false);

  // Handle ?new=true from CommandPalette
  createEffect(() => {
    if (searchParams.new === "true") {
      setShowNewDossierModal(true);
      setSearchParams({ new: undefined });
    }
  });

  const openNewDossierModal = () => setShowNewDossierModal(true);

  return (
    <AppShell title="Dossiers">
      <div class="p-6">
        <PageHeader
          title="Dossiers"
          description="Organize investigations and evidence"
          actions={
            <Button onClick={openNewDossierModal}>
              <Plus class="h-4 w-4 mr-2" />
              New Dossier
            </Button>
          }
        />

        <Suspense fallback={<DossiersSkeleton />}>
          <Show when={dossiers()} fallback={<DossiersSkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<FolderOpen class="h-12 w-12" />}
                    title="No dossiers yet"
                    description="Create your first dossier to start organizing your investigation"
                    actionLabel="Create Dossier"
                    onAction={openNewDossierModal}
                  />
                }
              >
                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <For each={items()}>
                    {(dossier) => (
                      <A href={`/investigate/dossiers/${dossier.id}`}>
                        <Card class="group h-full transition-all hover:border-primary/50 hover:shadow-md">
                          <CardHeader class="pb-3">
                            <div class="flex items-start justify-between gap-2">
                              <CardTitle class="text-lg leading-tight">{dossier.title}</CardTitle>
                              <FolderOpen class="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <Show when={dossier.description}>
                              <p class="text-sm text-muted-foreground mb-4 line-clamp-2">
                                {dossier.description}
                              </p>
                            </Show>

                            <div class="flex flex-wrap gap-1 mb-4">
                              <For each={dossier.tags.slice(0, 3)}>
                                {(tag) => (
                                  <Badge variant="secondary" class="text-xs font-mono">
                                    {tag}
                                  </Badge>
                                )}
                              </For>
                              <Show when={dossier.tags.length > 3}>
                                <Badge variant="outline" class="text-xs">
                                  +{dossier.tags.length - 3}
                                </Badge>
                              </Show>
                            </div>

                            <div class="flex items-center justify-between text-xs text-muted-foreground border-t pt-3">
                              <span class="flex items-center gap-1">
                                <FileText class="h-3 w-3" />
                                {dossier.itemCount} items
                              </span>
                              <span>Updated {formatRelativeTime(dossier.updatedAt)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </A>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </div>

      <Show when={showNewDossierModal()}>
        <Suspense>
          <NewDossierModal 
            open={showNewDossierModal()} 
            onOpenChange={setShowNewDossierModal} 
          />
        </Suspense>
      </Show>
    </AppShell>
  );
}

function DossiersSkeleton() {
  return (
    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <For each={[1, 2, 3, 4, 5, 6]}>
        {() => (
          <Card>
            <CardHeader class="pb-3">
              <Skeleton class="h-6 w-3/4" />
            </CardHeader>
            <CardContent class="space-y-3">
              <Skeleton class="h-4 w-full" />
              <div class="flex gap-1">
                <Skeleton class="h-5 w-16" />
                <Skeleton class="h-5 w-16" />
              </div>
              <Skeleton class="h-4 w-24" />
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
